import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Press } from '@dorixona/ui'
import { isApiError } from '@dorixona/api'
import { BackButton } from '../../ui/BackButton'
import { useSession } from '../../app/session'
import { Field } from '../auth/ui/Field'
import { PhoneField } from '../auth/ui/PhoneField'
import { OtpInput } from '../auth/ui/OtpInput'
import { displayPhone, isCompleteCode, isCompletePhone, toApiPhone } from '../auth/model'
import { profileView } from './model'
import { useRequestPhoneCode, useVerifyPhone, type PhoneChallenge } from './phone'

/** Текст ошибки по коду сервера; номер занят — своими словами, не «запись существует». */
function errorKey(error: unknown): string[] {
  const code = isApiError(error) ? error.code : 'network'
  if (code === 'conflict') return ['account:phone.taken']
  return [`errors:${code}`, 'errors:internal']
}

/**
 * Смена номера — своя страница: новый номер → код из СМС. Код набран —
 * подтверждаем сами, как при входе. Истёк — возвращаемся к номеру: новый код
 * нужен всё равно. «Назад» на шаге кода ведёт к номеру, а не со страницы.
 */
export function PhoneScreen() {
  const { t } = useTranslation(['account', 'auth', 'errors', 'common'])
  const navigate = useNavigate()
  const current = profileView(useSession().customer).phone
  const request = useRequestPhoneCode()
  const verify = useVerifyPhone()
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [challenge, setChallenge] = useState<PhoneChallenge | null>(null)
  const [resendIn, setResendIn] = useState(0)
  // isPending виден только после ре-рендера: без ref двойной клик шлёт два запроса (429).
  const requesting = useRef(false)
  const same = isCompletePhone(phone) && toApiPhone(phone) === current

  // Таймер повторной отправки: тикает раз в секунду и сам останавливается.
  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setTimeout(() => {
      setResendIn((left) => left - 1)
    }, 1000)
    return () => {
      clearTimeout(timer)
    }
  }, [resendIn])

  const toPhone = () => {
    setChallenge(null)
    setCode('')
    verify.reset()
  }

  const send = () => {
    if (requesting.current) return
    requesting.current = true
    verify.reset()
    request.mutate(toApiPhone(phone), {
      onSuccess: (next) => {
        setChallenge(next)
        setResendIn(next.resendIn)
        setCode('')
      },
      onSettled: () => {
        requesting.current = false
      },
    })
  }

  const confirm = (sent: PhoneChallenge, typed: string) => {
    verify.mutate(
      { challengeId: sent.challengeId, code: typed },
      {
        onSuccess: () => {
          // Номер сменился — в кабинет: карточка там уже с новым номером.
          void navigate('/profile', { replace: true })
        },
        onError: (error) => {
          // Истёкший код не повторить: нужен новый — начинаем с номера.
          if (isApiError(error) && error.code === 'otp_expired') setChallenge(null)
        },
      },
    )
  }

  const error = request.error ?? verify.error

  return (
    <section className="screen cart-wrap">
      <div className="mhead">
        <BackButton to="/profile/edit" onBack={challenge ? toPhone : undefined} />
        <h1>{t('account:phone.title')}</h1>
      </div>

      <div className="card phonecard">
        {challenge ? (
          <div className="phoneform">
            <p className="cardform-lead">
              {t('auth:code.subtitle', { phone: displayPhone(phone) })}
            </p>
            <OtpInput
              value={code}
              onChange={(next) => {
                verify.reset()
                setCode(next)
                if (isCompleteCode(next) && !verify.isPending) confirm(challenge, next)
              }}
              digitLabel={(position) => t('auth:code.digit', { position })}
              invalid={Boolean(verify.error)}
              disabled={verify.isPending}
              focusOnShow
            />
            <p className={error ? 'auth-error' : 'auth-note'} role={error ? 'alert' : undefined}>
              {error ? t(errorKey(error)) : t('auth:code.hint')}
            </p>
            <p className="auth-note">
              {resendIn > 0 ? (
                t('auth:code.resendIn', { seconds: resendIn })
              ) : (
                <Press
                  squish="chip"
                  className="linkbtn"
                  disabled={request.isPending}
                  onPress={send}
                >
                  {t('auth:code.resend')}
                </Press>
              )}
            </p>
            <div className="phoneform-foot">
              <Button
                loading={verify.isPending}
                disabled={!isCompleteCode(code)}
                onPress={() => {
                  confirm(challenge, code)
                }}
              >
                {t('auth:code.submit')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="phoneform">
            <p className="cardform-lead">{t('account:phone.lead')}</p>
            <Field
              label={t('account:phone.new')}
              error={same ? t('account:phone.same') : error ? t(errorKey(error)) : undefined}
            >
              {({ id, describedBy }) => (
                <PhoneField
                  id={id}
                  describedBy={describedBy}
                  value={phone}
                  placeholder={t('auth:phone.placeholder')}
                  onChange={(digits) => {
                    request.reset()
                    setPhone(digits)
                  }}
                  onSubmit={() => {
                    if (isCompletePhone(phone) && !same) send()
                  }}
                />
              )}
            </Field>
            <div className="phoneform-foot">
              <Button
                loading={request.isPending}
                disabled={!isCompletePhone(phone) || same}
                onPress={send}
              >
                {t('account:phone.send')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
